import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  useTheme,
  styled,
  Slide,
  Fade,
} from "@mui/material";
import { useInView } from "../hooks/useInView";
import image from "../assets/images/achievments/ag.webp";

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bgX" && prop !== "bgY",
})<{ bgX: string; bgY: string }>(({ theme, bgX, bgY }) => ({
  position: "relative",
  overflow: "hidden",
  backgroundColor: theme.palette.primary.hero,
  color: theme.palette.primary.main,
  borderRadius: 12,
  padding: theme.spacing(4),
  height: "100%",
  maxWidth: 250,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.2)",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.03)",
  },

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `url(${image})`,
    backgroundSize: "1065px 500px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: `${bgX} ${bgY}`,
    opacity: 0.2,
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.focus,
  fontWeight: 600,
  fontSize: "1.25rem",
  marginBottom: theme.spacing(1),
}));

const DividerLine = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "0.5px",
  backgroundColor: theme.palette.primary.focus,
  margin: theme.spacing(2, 0),
}));

const useCountUp = (target: number, start: boolean, duration = 2500) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startValue = 0;
    const increment = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      startValue += increment;
      if (startValue >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(startValue);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [start, target, duration]);

  return count;
};

const AchievementsSection = () => {
  const theme = useTheme();
  const { ref, inView } = useInView({ threshold: 0.3 });

  const data = [
    {
      title: "Hypotheses Processed",
      description:
        "We calculate probability scores for scientific hypotheses using statistics to aggregate all evidence.",
      value: "Coming soon",
      suffix: "",
    },
    {
      title: "Human Experiences",
      description: "Unique personal experiences have been collected.",
      value: 137544,
    },
    {
      title: "Research Papers",
      description:
        "Our knowledge graph includes research from biology, medicine, physics, psychology, religious studies, metaphysics, and many more fields.",
      value: 26399175,
    },
    {
      title: "AI Accuracy Score",
      description:
        "Our AI truth-estimation engine achieves highly transparent and evidence-weighted conclusions.",
      value: "Coming soon",
      // suffix: "%",
    },
  ];

  return (
    <Box
      component="section"
      ref={ref}
      sx={{
        py: { xs: 6, md: 13 },
        px: { xs: 2, sm: 4, md: 20 },
        backgroundColor: theme.palette.cosmic.primary,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25' viewBox='0 0 1600 800'%3E%3Cg %3E%3Cpath fill='%23FFF4D6' d='M486 705.8c-109.3-21.8-223.4-32.2-335.3-19.4C99.5 692.1 49 703 0 719.8V800h843.8c-115.9-33.2-230.8-68.1-347.6-92.2C492.8 707.1 489.4 706.5 486 705.8z'/%3E%3Cpath fill='%23FFEEBC' d='M1600 0H0v719.8c49-16.8 99.5-27.8 150.7-33.5c111.9-12.7 226-2.4 335.3 19.4c3.4 0.7 6.8 1.4 10.2 2c116.8 24 231.7 59 347.6 92.2H1600V0z'/%3E%3Cpath fill='%23FFE7A0' d='M478.4 581c3.2 0.8 6.4 1.7 9.5 2.5c196.2 52.5 388.7 133.5 593.5 176.6c174.2 36.6 349.5 29.2 518.6-10.2V0H0v574.9c52.3-17.6 106.5-27.7 161.1-30.9C268.4 537.4 375.7 554.2 478.4 581z'/%3E%3Cpath fill='%23FFE085' d='M0 0v429.4c55.6-18.4 113.5-27.3 171.4-27.7c102.8-0.8 203.2 22.7 299.3 54.5c3 1 5.9 2 8.9 3c183.6 62 365.7 146.1 562.4 192.1c186.7 43.7 376.3 34.4 557.9-12.6V0H0z'/%3E%3Cpath fill='%23FFD96B' d='M181.8 259.4c98.2 6 191.9 35.2 281.3 72.1c2.8 1.1 5.5 2.3 8.3 3.4c171 71.6 342.7 158.5 531.3 207.7c198.8 51.8 403.4 40.8 597.3-14.8V0H0v283.2C59 263.6 120.6 255.7 181.8 259.4z'/%3E%3Cpath fill='%23FFE085' d='M1600 0H0v136.3c62.3-20.9 127.7-27.5 192.2-19.2c93.6 12.1 180.5 47.7 263.3 89.6c2.6 1.3 5.1 2.6 7.7 3.9c158.4 81.1 319.7 170.9 500.3 223.2c210.5 61 430.8 49 636.6-16.6V0z'/%3E%3Cpath fill='%23FFE7A0' d='M454.9 86.3C600.7 177 751.6 269.3 924.1 325c208.6 67.4 431.3 60.8 637.9-5.3c12.8-4.1 25.4-8.4 38.1-12.9V0H288.1c56 21.3 108.7 50.6 159.7 82C450.2 83.4 452.5 84.9 454.9 86.3z'/%3E%3Cpath fill='%23FFEEBC' d='M1600 0H498c118.1 85.8 243.5 164.5 386.8 216.2c191.8 69.2 400 74.7 595 21.1c40.8-11.2 81.1-25.2 120.3-41.7V0z'/%3E%3Cpath fill='%23FFF4D6' d='M1397.5 154.8c47.2-10.6 93.6-25.3 138.6-43.8c21.7-8.9 43-18.8 63.9-29.5V0H643.4c62.9 41.7 129.7 78.2 202.1 107.4C1020.4 178.1 1214.2 196.1 1397.5 154.8z'/%3E%3Cpath fill='%23F4F6FA' d='M1315.3 72.4c75.3-12.6 148.9-37.1 216.8-72.4h-723C966.8 71 1144.7 101 1315.3 72.4z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
      }}
    >
      {/* TOP ROW */}
      <Grid container spacing={6} alignItems="flex-start" sx={{ mb: 10 }}>
        <Grid item xs={12} md={4} component="div" {...({} as any)}>
          <Typography
            variant="subtitle1"
            sx={{
              letterSpacing: 2,
              fontWeight: 500,
              color: theme.palette.primary.hover,
              mb: 1,
              ml: 3,
            }}
          >
            OUR ACHIEVEMENTS
          </Typography>
        </Grid>
        <Grid item xs={12} md={8} component="div" {...({} as any)}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.hover,
              letterSpacing: 2,
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Smart AI Solutions That <br />
            Deliver Real Results
          </Typography>
        </Grid>
      </Grid>

      {/* BOTTOM ROW - CARDS */}
      <Grid
        container
        spacing={4}
        alignItems="center"
        justifyContent="center"
        component="div"
        {...({} as any)}
      >
        {data.map((item, index) => {
          const isNumber = typeof item.value === "number";
          const count = isNumber
            ? useCountUp(item.value as number, inView)
            : item.value;
          const col = index % 4;
          const row = Math.floor(index / 4);
          const bgX = `-${col * 250}px`;
          const bgY = `-${row * 250}px`;

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={index}
              component="div"
              {...({} as any)}
            >
              <Slide
                direction="up"
                in={inView}
                timeout={1000}
                style={{ transitionDelay: `${index * 300}ms` }}
              >
                <Fade in={inView} timeout={1000 + index * 300}>
                  <CardContainer bgX={bgX} bgY={bgY}>
                    <Box>
                      <Title>{item.title}</Title>
                      <Typography variant="h6">{item.description}</Typography>
                    </Box>
                    <DividerLine />
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 500,
                        mt: 3,
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      {isNumber ? count.toLocaleString() : count}
                      {isNumber && (item.suffix ?? "+")}
                    </Typography>
                  </CardContainer>
                </Fade>
              </Slide>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AchievementsSection;
